import { Action, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, take, takeLatest } from 'typed-redux-saga';

import {
    getIsMediaDevicesSyncEnabled,
    selectIsMessengerCallIntegration,
} from '../../../shared/features/config/selectors';
import { deviceManager } from '../../../shared/features/goloom-sdk';
import { parentFrameCommunicator } from '../../../shared/features/parent-frame-communicator';
import {
    getDevices,
    getDeviceSelector,
    getHasDeviceAlreadySetted,
    getHasDevices,
    setActiveAudioInputDeviceAction,
    setActiveAudioOutputDeviceAction,
    setActiveVideoInputDeviceAction,
    setAudioInputDeviceAction,
    setAudioOutputDeviceAction,
    SetDeviceActionPayload,
    setDevicesAction,
    setVideoInputDeviceAction,
} from '../../../shared/features/settings';
import { findDeviceByLabel, getDeviceLabelOrDefault } from '../../../shared/features/settings/sagas';
import { ChannelHolder } from '../../../shared/helpers/channelHolder';
import { takeLatestPerKey } from '../../../shared/helpers/redux-saga';
import { handleDeviceProcessorsSync } from './handle-device-processors-sync';
import { sendAudioInputDeviceStatusAction, sendVideoInputDeviceStatusAction, syncMediaDeviceAction } from './slice';
import { OutgoingEvents, SetMuteOutgoingPayload, SyncMediaDevicePayload } from './types';

function* syncFromHostSaga({ payload: { deviceKind, commonDeviceId } }: PayloadAction<SyncMediaDevicePayload>) {
    const hasDevices = yield* select(getHasDevices);

    if (!hasDevices) {
        yield take(setDevicesAction);
    }

    const devices = yield* select(getDevices);

    const device = yield* call(findDeviceByLabel, devices, commonDeviceId, deviceKind);
    if (!device) {
        return;
    }

    const isDeviceAlreadySetted = yield* select((state) => getHasDeviceAlreadySetted(state, device));

    if (isDeviceAlreadySetted) {
        return;
    }

    switch (device.kind) {
        case 'audioinput':
            yield* put(setAudioInputDeviceAction(device.deviceId, { isSyncFromHost: true }));

            break;
        case 'audiooutput':
            yield* put(setAudioOutputDeviceAction(device.deviceId, { isSyncFromHost: true }));

            break;
        case 'videoinput':
            yield* put(setVideoInputDeviceAction(device.deviceId, { isSyncFromHost: true }));
    }
}

function* syncDeviceKindSaga(kind: MediaDeviceKind, deviceId: string, doNotUpdateLocalStore = false) {
    const hasDevices = yield* select(getHasDevices);

    if (!hasDevices) {
        yield take(setDevicesAction);
    }

    const device = yield* select(getDeviceSelector, deviceId, kind);

    if (!device) {
        return;
    }

    parentFrameCommunicator.sendMessage('messenger', {
        event: OutgoingEvents.SET_MEDIA_DEVICE,
        payload: { deviceKind: device.kind, commonDeviceId: getDeviceLabelOrDefault(device), doNotUpdateLocalStore },
    });
}

function* syncDeviceKindWatcher(
    kind: MediaDeviceKind,
    setActionCreator:
        | typeof setAudioInputDeviceAction
        | typeof setAudioOutputDeviceAction
        | typeof setVideoInputDeviceAction,
    setActiveActionCreator:
        | typeof setActiveAudioInputDeviceAction
        | typeof setActiveAudioOutputDeviceAction
        | typeof setActiveVideoInputDeviceAction
) {
    yield* all([
        takeLatest(
            (action: Action) => setActionCreator.match(action) && !action.meta?.isSyncFromHost,
            function* (action: PayloadAction<SetDeviceActionPayload>) {
                yield* syncDeviceKindSaga(kind, action.payload.deviceId);
            }
        ),
        takeLatest(setActiveActionCreator, function* (action: PayloadAction<string>) {
            yield* syncDeviceKindSaga(kind, action.payload, true);
        }),
    ]);
}

function* sendDeviceStatusChanged({ payload: { type, value } }: PayloadAction<SetMuteOutgoingPayload>) {
    yield* call(parentFrameCommunicator.sendMessage, 'messenger', {
        event: OutgoingEvents.SET_MUTE,
        payload: {
            type,
            value,
        },
    });
}

function* handleAudioStatusChange(status: boolean) {
    yield* put(
        sendAudioInputDeviceStatusAction({
            type: 'audio',
            value: !status,
        })
    );
}

function* handleVideoStatusChange(status: boolean) {
    yield* put(
        sendVideoInputDeviceStatusAction({
            type: 'video',
            value: !status,
        })
    );
}

function* syncDeviceStatusToHost() {
    const isMessengerCallIntegration = yield* select(selectIsMessengerCallIntegration);

    if (!isMessengerCallIntegration) {
        return;
    }
    const channelHolder = new ChannelHolder();
    try {
        yield* all([
            channelHolder.takeLatest(deviceManager.onAudioStatusChanged, handleAudioStatusChange),
            channelHolder.takeLatest(deviceManager.onVideoStatusChanged, handleVideoStatusChange),
            takeLatest(sendAudioInputDeviceStatusAction, sendDeviceStatusChanged),
            takeLatest(sendVideoInputDeviceStatusAction, sendDeviceStatusChanged),
        ]);
    } finally {
        channelHolder.close();
    }
}
function* syncToHost() {
    yield* all([
        syncDeviceKindWatcher('audioinput', setAudioInputDeviceAction, setActiveAudioInputDeviceAction),
        syncDeviceKindWatcher('audiooutput', setAudioOutputDeviceAction, setActiveAudioOutputDeviceAction),
        syncDeviceKindWatcher('videoinput', setVideoInputDeviceAction, setActiveVideoInputDeviceAction),
        syncDeviceStatusToHost(),
        handleDeviceProcessorsSync(),
    ]);
}

export function* syncMediaDevices() {
    const isMediaDevicesSyncEnabled = yield* select(getIsMediaDevicesSyncEnabled);

    if (!isMediaDevicesSyncEnabled) {
        return;
    }

    yield* all([
        takeLatestPerKey(syncMediaDeviceAction, syncFromHostSaga, (action) => action.payload.deviceKind),
        syncToHost(),
    ]);
}
